// Contrato de errores de las escrituras de Inventario: cada code con su mensaje y la acción del front.
import { ApiError, userMessageOf } from '@/api/errors';
import { isRetryableWriteError, type IdempotentAttempt } from '@/lib/idempotentAttempt';

/** Qué hace la pantalla con el error. */
export type InventoryErrorAction =
    | 'FIX_FORM'
    | 'FIX_PIECE'
    | 'FIX_QUANTITY'
    | 'PICK_OTHER_STORE'
    | 'WAIT_RESERVATION'
    | 'SPLIT'
    | 'RELOAD'
    | 'NEW_KEY'
    | 'RETRY'
    | 'HIDE'
    | 'INFORM';

export interface InventoryWriteError {
    code: string;
    message: string;
    action: InventoryErrorAction;
    /** Reintentar con la MISMA llave de idempotencia. */
    retryable: boolean;
}

interface CodeRule {
    message: string;
    action: InventoryErrorAction;
}

const RELOAD_PIECE = 'Una de las piezas ya no está disponible. Recarga y vuelve a elegirla.';
const RESERVED =
    'Hay piezas o unidades apartadas en una venta en curso. Espera a que se cierre o libera el apartado.';
const NO_PERMISSION = 'No tienes permiso para esta acción.';

const RULE_BY_CODE: Record<string, CodeRule> = {
    SAME_STORE_TRANSFER: {
        message: 'La tienda destino debe ser distinta a la de origen. Elige otra.',
        action: 'PICK_OTHER_STORE',
    },
    STORE_NOT_IN_RETAILER: {
        message: 'La tienda elegida no es de tu cadena. Elige otra.',
        action: 'PICK_OTHER_STORE',
    },
    VALIDATION_ERROR: {
        message:
            'Revisa los datos del formulario: hay campos inválidos o la cantidad pasa de 10 000.',
        action: 'FIX_FORM',
    },
    FORBIDDEN: { message: NO_PERMISSION, action: 'HIDE' },
    PERMISSION_DENIED: { message: NO_PERMISSION, action: 'HIDE' },
    MISSING_RETAILER_SCOPE: {
        message: 'Tu usuario no tiene una cadena asignada. Contacta al administrador.',
        action: 'HIDE',
    },
    PRODUCT_NOT_FOUND: {
        message: 'Este SKU ya no existe o está inactivo. Recarga la lista.',
        action: 'RELOAD',
    },
    TRANSFER_NOT_FOUND: {
        message: 'Este traspaso ya no existe. Recarga la lista.',
        action: 'RELOAD',
    },
    DUPLICATE_SKU: { message: 'Ya existe un producto con este SKU. Usa otro.', action: 'FIX_FORM' },
    KIT_PHONE_LIMIT_EXCEEDED: {
        message: 'Un kit solo puede llevar un teléfono. Deja uno y quita los demás.',
        action: 'FIX_FORM',
    },
    IMEI_LUHN_FAILED: {
        message: 'Uno de los IMEI no es válido. Revisa los 15 dígitos de la pieza.',
        action: 'FIX_PIECE',
    },
    DUPLICATE_IDENTIFIER: {
        message: 'Una de las piezas ya está registrada. Revisa los IMEI o números de serie.',
        action: 'FIX_PIECE',
    },
    PRODUCT_HAS_STOCK: {
        message:
            'No se puede desactivar: todavía tiene saldo, apartados o traspasos abiertos en alguna tienda.',
        action: 'INFORM',
    },
    INSUFFICIENT_STOCK: {
        message: 'No hay stock suficiente en la tienda. Corrige la cantidad.',
        action: 'FIX_QUANTITY',
    },
    PIECE_RESERVED_CONFLICT: { message: RESERVED, action: 'WAIT_RESERVATION' },
    INVALID_QUANTITY: { message: RESERVED, action: 'WAIT_RESERVATION' },
    TRANSFER_NOT_OPEN: {
        message: 'Este traspaso ya se recibió o se canceló. Recarga la lista.',
        action: 'RELOAD',
    },
    IDENTIFIER_NOT_AVAILABLE: { message: RELOAD_PIECE, action: 'RELOAD' },
    IDEMPOTENCY_KEY_REUSED: {
        message: 'La solicitud se repitió con otros datos. Envíala de nuevo.',
        action: 'NEW_KEY',
    },
    MOVEMENT_TOO_LARGE: {
        message: 'Son demasiadas piezas para una sola operación. Divídela en tandas de hasta 30.',
        action: 'SPLIT',
    },
    IDENTIFIER_MISMATCH: {
        message: 'Una de las piezas no es de esta tienda o de este SKU. Corrígela.',
        action: 'FIX_PIECE',
    },
    RETAIL_ORG_UNAVAILABLE: {
        message: 'No pudimos validar la tienda en este momento. Intenta de nuevo.',
        action: 'RETRY',
    },
};

const RETRY_MESSAGE =
    'Algo falló al guardar. Intenta de nuevo: la operación no se registrará dos veces.';

/** Traduce el error de una escritura del catálogo a mensaje y acción. */
export function inventoryWriteErrorOf(error: unknown): InventoryWriteError {
    const retryable = isRetryableWriteError(error);
    const code = error instanceof ApiError ? error.code : 'UNKNOWN_ERROR';
    if (error instanceof ApiError && Object.hasOwn(RULE_BY_CODE, code)) {
        return { code, retryable, ...RULE_BY_CODE[code] };
    }
    if (retryable) return { code, retryable, message: RETRY_MESSAGE, action: 'RETRY' };
    return { code, retryable, message: userMessageOf(error), action: 'INFORM' };
}

/** Traduce el error y, si el silo rechazó la llave por reusada, la renueva para el siguiente envío. */
export function applyWriteError(
    error: unknown,
    attempt: IdempotentAttempt,
    payload: unknown,
): InventoryWriteError {
    const mapped = inventoryWriteErrorOf(error);
    if (mapped.action === 'NEW_KEY') attempt.renew(payload);
    return mapped;
}

const DEACTIVATE_TOO_LARGE =
    'Este producto tiene saldo en demasiadas tiendas para desactivarlo de una vez; contacta a soporte.';

/** «Desactivar» no se parte en tandas: MOVEMENT_TOO_LARGE ahí se escala a soporte. */
export function deactivateErrorOf(error: unknown): InventoryWriteError {
    const mapped = inventoryWriteErrorOf(error);
    if (mapped.code !== 'MOVEMENT_TOO_LARGE') return mapped;
    return { ...mapped, message: DEACTIVATE_TOO_LARGE, action: 'INFORM' };
}
